import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Calendar, CheckSquare, Clock, FileText, MessageCircle, Info, Check, RefreshCw, CheckCircle, XCircle, Package, Plus, Server, ChevronDown, AlertCircle, X } from 'lucide-react';
import { ToolSelection } from './ToolSelection';
import { AddMCPServerModal } from './AddMCPServerModal';
export const CreateTask = ({
  onClose,
  onCreateTask
}) => {
  const [taskDescription, setTaskDescription] = useState('');
  const handleInputChange = e => {
    setTaskDescription(e.target.value);
  };
  const handleCreateFromDescription = () => {
    if (taskDescription.trim()) {
      onCreateTask(taskDescription);
    }
  };
  const handleSelectTemplate = template => {
    // Generate detailed step-by-step instructions based on the template
    const detailedDescription = generateDetailedInstructions(template);
    onCreateTask(detailedDescription, template.name);
  };
  // Function to generate detailed step-by-step instructions based on the template
  const generateDetailedInstructions = template => {
    switch (template.id) {
      case 1:
        // Handle negative reviews
        return `1. Monitor all customer reviews across platforms (website, app, social media).
2. Identify reviews with ratings below 3 stars or negative sentiment.
3. For each negative review:
   a. Analyze the specific complaint or issue mentioned.
   b. Generate a personalized response acknowledging their concerns.
   c. Create a unique discount code (10-15% off their next purchase).
   d. Include the discount code in your response along with an apology.
   e. Follow up after 7 days if the customer hasn't responded.
4. Track which customers redeem their discount codes.
5. Compile a weekly report of common issues mentioned in negative reviews.`;
      case 2:
        // Order low-stock items
        return `1. Check inventory levels daily for all products.
2. Identify items with stock levels below the minimum threshold (less than 20 units).
3. For each low-stock item:
   a. Retrieve the supplier information from the database.
   b. Check the typical lead time for delivery.
   c. Calculate the optimal reorder quantity based on historical sales data.
   d. Generate a purchase order with item details, quantities, and delivery address.
   e. Send the order to the appropriate supplier with priority level.
4. Track all pending orders and expected delivery dates.
5. Alert management if any critical items will be out of stock before new shipments arrive.`;
      case 3:
        // Summarize feedback
        return `1. Collect all event feedback submissions from the past week.
2. Categorize feedback into key areas (venue, content, speakers, logistics, etc.).
3. For each category:
   a. Calculate average ratings and identify trends.
   b. Extract common themes from written comments.
   c. Highlight specific quotes that represent major sentiments.
   d. Compare with previous event metrics where available.
4. Identify the top 3 areas of improvement based on lowest scores.
5. Create an action plan with specific recommendations for each improvement area.
6. Generate a comprehensive report with visualizations of the data.
7. Distribute the report to the event planning team and stakeholders.`;
      case 4:
        // Report bookings
        return `1. At the end of each week, collect all booking data from the system.
2. Analyze the following metrics:
   a. Total number of new bookings made.
   b. Total number of cancellations and reasons given.
   c. Revenue generated from bookings.
   d. Most popular booking times/days.
   e. Services with highest and lowest demand.
3. Compare current week's data with the previous 4 weeks to identify trends.
4. Generate charts and visualizations for key metrics.
5. Highlight any unusual patterns or concerns that require attention.
6. Create a detailed PDF report with all findings and recommendations.
7. Email the report to management and department heads every Monday morning.`;
      case 5:
        // Recover abandoned carts
        return `1. Monitor the e-commerce system for shopping carts abandoned within the last 24 hours.
2. For each abandoned cart:
   a. Wait 4 hours to see if the customer returns naturally.
   b. Retrieve the customer's email and cart contents.
   c. Generate a personalized email reminding them of their items.
   d. Include product images and direct link to restore their cart.
   e. Offer a limited-time discount (5-10%) or free shipping as incentive.
3. If no response after 3 days, send a follow-up email with:
   a. Increased urgency (limited stock, sale ending soon).
   b. Alternative product recommendations based on their cart.
4. Track recovery rate and calculate ROI of the recovery campaign.
5. Compile weekly report of most commonly abandoned products.`;
      case 6:
        // Analyse feedback after events
        return `1. Gather all post-event survey responses and feedback forms.
2. Organize feedback into categories (content, speakers, venue, food, technology, etc.).
3. For each category:
   a. Calculate average satisfaction scores.
   b. Extract common themes from written comments.
   c. Identify the most frequently mentioned positives and negatives.
   d. Compare results with previous events or industry benchmarks.
4. Generate word clouds and sentiment analysis from open-ended responses.
5. Create a prioritized list of improvement opportunities.
6. Develop specific, actionable recommendations for each improvement area.
7. Compile findings into a comprehensive report with visual elements.
8. Present insights to event planning team with suggested action items.`;
      default:
        return template.description;
    }
  };
  const templates = [{
    id: 1,
    name: 'Handle negative reviews',
    description: 'Follow up on bad reviews with a discount to win back customers',
    category: 'all'
  }, {
    id: 2,
    name: 'Order low-stock items',
    description: 'Automatically order items when inventory is low',
    category: 'stores'
  }, {
    id: 3,
    name: 'Summarize feedback',
    description: 'Collect and summarize event feedback to spot areas for improvement',
    category: 'all'
  }, {
    id: 4,
    name: 'Report bookings',
    description: 'Generate weekly reports on new bookings and cancellations',
    category: 'bookings'
  }, {
    id: 5,
    name: 'Recover abandoned carts',
    description: "Send reminder emails to users who didn't complete checkout",
    category: 'stores'
  }, {
    id: 6,
    name: 'Analyse feedback after events',
    description: 'Collect and summarize event feedback to spot areas for improvement',
    category: 'all'
  }];
  const [activeCategory, setActiveCategory] = useState('all');
  return <div className="max-w-full" data-id="element-828">
      <div className="flex items-center mb-6" data-id="element-829">
        <button onClick={onClose} className="mr-3 text-gray-500 hover:text-gray-700" data-id="element-830">
          <ArrowLeft className="w-5 h-5" data-id="element-831" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-832">
          Create Agent Workflow
        </h1>
      </div>
      <div className="flex flex-col items-center max-w-3xl mx-auto mb-12" data-id="element-833">
        <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-834">
          What can I handle for you?
        </h2>
        <p className="text-gray-600 text-center mb-8" data-id="element-835">
          Briefly describe a task you want me to do – I'll help you set it up.
        </p>
        <div className="w-full relative mb-8" data-id="element-836">
          <textarea className="w-full p-4 pr-12 border border-gray-200 rounded-lg h-24 resize-none" placeholder="Describe your workflow" value={taskDescription} onChange={handleInputChange} data-id="element-837" />
          <button className={`absolute right-3 bottom-3 p-2 rounded-full ${taskDescription.trim() ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-400'}`} disabled={!taskDescription.trim()} onClick={handleCreateFromDescription} data-id="element-838">
            <ArrowRight className="w-4 h-4" data-id="element-839" />
          </button>
        </div>
        <div className="w-full" data-id="element-840">
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-841">
            <button className={`px-4 py-2 rounded-full text-sm ${activeCategory === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setActiveCategory('all')} data-id="element-842">
              View All
            </button>
            <button className={`px-4 py-2 rounded-full text-sm ${activeCategory === 'stores' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setActiveCategory('stores')} data-id="element-843">
              Stores
            </button>
            <button className={`px-4 py-2 rounded-full text-sm ${activeCategory === 'bookings' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setActiveCategory('bookings')} data-id="element-844">
              Bookings
            </button>
            <button className={`px-4 py-2 rounded-full text-sm ${activeCategory === 'pricing' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => setActiveCategory('pricing')} data-id="element-845">
              Pricing plans
            </button>
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-846">
            Or pick from ready-to-go templates tailored to different business
            categories
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-847">
            {templates.filter(template => activeCategory === 'all' || template.category === activeCategory).map(template => <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => handleSelectTemplate(template)} data-id="element-848">
                  <h4 className="font-medium text-gray-800 mb-2" data-id="element-849">
                    {template.name}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-850">
                    {template.description}
                  </p>
                  <button className="mt-3 text-sm text-gray-700 flex items-center" data-id="element-851">
                    Select <ArrowRight className="w-3 h-3 ml-1" data-id="element-852" />
                  </button>
                </div>)}
          </div>
        </div>
      </div>
    </div>;
};
// Add a new component for the task setup screen based on the screenshots
export const TaskSetupScreen = ({
  task,
  onSave,
  onCancel
}) => {
  const [workflowName, setWorkflowName] = useState(task?.title || task?.name || '');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [instructions, setInstructions] = useState(task?.instructions || `1. Analyze the inventory data to identify any items with stock levels below 20 units.
2. For each item identified, determine the corresponding supplier responsible for restocking that item.
3. Draft a personalized restock order email for each supplier, including the following details:
   - The item(s) that need restocking.
   - The current stock levels and the recommended restock quantity.
   - A polite request to confirm the order and provide an estimated delivery date.
4. Send the email to the respective supplier.`);
  const [requireApprovals, setRequireApprovals] = useState(true);
  const [triggerEnabled, setTriggerEnabled] = useState(true);
  const [showTestLog, setShowTestLog] = useState(false);
  const [showAddMCPModal, setShowAddMCPModal] = useState(false);
  // Define testLogData array
  const testLogData = [{
    id: 1,
    title: 'Perform trigger action',
    details: [{
      icon: <Package className="w-4 h-4 text-gray-600" data-id="element-853" />,
      title: 'Checkout & Orders',
      subtitle: 'Order placed'
    }],
    success: 'Agent triggered.'
  }, {
    id: 2,
    status: 'success',
    title: 'Searching inventory data',
    description: 'Current inventory information retrieved, providing insights for restock orders.'
  }, {
    id: 3,
    status: 'success',
    title: 'Reading full documentation',
    description: 'Successfully retrieved the method for querying inventory items, including details on filtering, sorting, and pagination.'
  }, {
    id: 4,
    status: 'error',
    title: 'Querying inventory items',
    description: 'Failed to query inventory items due to an incorrect catalog version for the API call.'
  }, {
    id: 5,
    status: 'success',
    title: 'Searching Wix REST documentation',
    description: 'Successfully retrieved documentation on inventory query use cases and API details for Catalog V1.'
  }, {
    id: 6,
    status: 'success',
    title: 'Getting inventory API documentation',
    description: 'The API schema for querying products was successfully retrieved, detailing methods, parameters, and response types.'
  }, {
    id: 7,
    status: 'success',
    title: 'Querying products',
    description: 'Detailed product information and stock data retrieved successfully, including 14 products with their respective prices and availability.'
  }, {
    id: 8,
    status: 'success',
    title: 'Analyzing inventory levels',
    description: 'Identified 3 products with stock levels below the threshold of 20 units: "Organic Cotton T-shirt" (8 units), "Recycled Denim Jeans" (12 units), and "Bamboo Fiber Socks" (15 units).'
  }, {
    id: 9,
    status: 'success',
    title: 'Finding supplier information',
    description: 'Located supplier details for the low-stock items: EcoTextiles (T-shirts), SustainableDenim Co. (Jeans), and GreenFiber Inc. (Socks).'
  }, {
    id: 10,
    status: 'success',
    title: 'Drafting restock emails',
    description: 'Created personalized email templates for each supplier with current stock levels, recommended reorder quantities, and request for delivery estimates.'
  }, {
    id: 11,
    status: 'summary',
    title: 'Test completed successfully',
    description: 'All steps executed properly. The agent successfully identified low-stock items, found suppliers, and prepared restock emails. Ready for production use.'
  }];
  const handleRunTest = () => {
    setShowTestLog(true);
  };
  const handleTitleChange = e => {
    setWorkflowName(e.target.value);
  };
  const handleTitleBlur = () => {
    setIsEditingTitle(false);
  };
  const handleTitleKeyDown = e => {
    if (e.key === 'Enter') {
      setIsEditingTitle(false);
    }
  };
  const handleAddMCPServer = serverData => {
    console.log('Adding MCP server:', serverData);
    // Here you would implement the actual logic to add the server
  };
  return <div className="max-w-full" data-id="element-854">
      <div className="flex items-center justify-between mb-6" data-id="element-855">
        <div className="flex items-center" data-id="element-856">
          <button onClick={onCancel} className="mr-3 text-gray-500 hover:text-gray-700" data-id="element-857">
            <ArrowLeft className="w-5 h-5" data-id="element-858" />
          </button>
          {isEditingTitle ? <input type="text" className="text-2xl font-bold text-gray-800 border-b border-gray-300 focus:outline-none focus:border-gray-500 bg-transparent" value={workflowName} onChange={handleTitleChange} onBlur={handleTitleBlur} onKeyDown={handleTitleKeyDown} autoFocus data-id="element-859" /> : <h1 className="text-2xl font-bold text-gray-800 cursor-text border-b border-dotted border-gray-400 pb-0.5" onClick={() => setIsEditingTitle(true)} data-id="element-860">
              {workflowName}
            </h1>}
        </div>
        <div className="flex items-center" data-id="element-861">
          <button onClick={onCancel} className="px-6 py-2 border border-gray-200 rounded-md mr-2 text-sm font-medium" data-id="element-862">
            Cancel
          </button>
          <button className="px-6 py-2 bg-gray-800 text-white rounded-md text-sm font-medium" onClick={onSave} data-id="element-863">
            Save
          </button>
        </div>
      </div>
      {/* Main content area with 2:1 proportions */}
      <div className="flex gap-6" data-id="element-864">
        {/* Left column - Task definition (2/3 width) */}
        <div className="w-2/3 flex flex-col" data-id="element-865">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6" data-id="element-866">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-867">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-868">
                Define instructions
              </h2>
            </div>
            <div className="p-6" data-id="element-869">
              <div className="mb-6" data-id="element-870">
                <div className="flex items-center mb-2" data-id="element-871">
                  <label className="block text-sm text-gray-600" data-id="element-872">
                    Instructions
                  </label>
                  <Info className="w-4 h-4 text-gray-400 ml-1" data-id="element-873" />
                </div>
                <textarea className="w-full p-3 border border-gray-200 rounded-md h-48 resize-none text-sm" value={instructions} onChange={e => setInstructions(e.target.value)} data-id="element-874"></textarea>
                <div className="mt-2 text-xs text-gray-500" data-id="element-875">
                  AI can make mistakes, so double-check that the results are
                  accurate before using them.
                </div>
              </div>
              <div className="flex items-center" data-id="element-876">
                <div className={`w-10 h-5 ${requireApprovals ? 'bg-gray-800' : 'bg-gray-300'} rounded-full relative mr-3 cursor-pointer`} onClick={() => setRequireApprovals(!requireApprovals)} data-id="element-877">
                  <div className={`absolute ${requireApprovals ? 'right-1' : 'left-1'} top-1 w-3 h-3 bg-white rounded-full transition-all`} data-id="element-878"></div>
                </div>
                <div data-id="element-879">
                  <div className="text-sm font-medium text-gray-700" data-id="element-880">
                    Require approvals
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-881">
                    Review and confirm agent actions before it sends emails or
                    makes changes to your site.
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Setup trigger panel */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6" data-id="element-882">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center" data-id="element-883">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-884">
                Setup trigger
              </h2>
              <Info className="w-4 h-4 text-gray-400 ml-1" data-id="element-885" />
            </div>
            <div className="p-4" data-id="element-886">
              <p className="text-sm text-gray-600 mb-3" data-id="element-887">
                The agent will execute instructions automatically whenever the
                selected trigger occurs.
              </p>
              <div data-id="element-888">
                <div className="text-sm font-medium text-gray-700 mb-2" data-id="element-889">
                  Trigger
                </div>
                <div className="border border-gray-200 rounded-md p-3" data-id="element-890">
                  <div className="flex items-center justify-between" data-id="element-891">
                    <div className="flex items-center" data-id="element-892">
                      <div className={`w-10 h-5 ${triggerEnabled ? 'bg-gray-800' : 'bg-gray-300'} rounded-full relative mr-3 cursor-pointer`} onClick={() => setTriggerEnabled(!triggerEnabled)} data-id="element-893">
                        <div className={`absolute ${triggerEnabled ? 'right-1' : 'left-1'} top-1 w-3 h-3 bg-white rounded-full transition-all`} data-id="element-894"></div>
                      </div>
                      <div className="flex items-center" data-id="element-895">
                        <div className="w-8 h-8 bg-gray-200 rounded-md flex items-center justify-center mr-3" data-id="element-896">
                          <FileText className="w-4 h-4 text-gray-600" data-id="element-897" />
                        </div>
                        <div data-id="element-898">
                          <div className="text-sm font-medium text-gray-800" data-id="element-899">
                            Wix Forms
                          </div>
                          <div className="text-xs text-gray-600" data-id="element-900">
                            Form submitted
                          </div>
                        </div>
                      </div>
                    </div>
                    <button className="px-3 py-1 border border-gray-200 rounded-md text-xs font-medium" data-id="element-901">
                      Edit Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Tool Selection Section */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6" data-id="element-902">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-903">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-904">
                Manage tools
              </h2>
              <Info className="w-4 h-4 text-gray-400" data-id="element-905" />
            </div>
            <div className="p-3" data-id="element-906">
              <ToolSelection onClose={() => {}} // Empty function since we're not hiding it
            onAddMCPServer={() => setShowAddMCPModal(true)} hideCloseButton={true} data-id="element-907" />
              {/* Custom styling for the Add MCP Server button */}
              <style jsx global data-id="element-908">{`
                /* Target the Add MCP Server button to make it more compact */
                [role='button']:has(svg[data-lucide='server']) {
                  width: auto !important;
                  display: inline-flex !important;
                  padding: 6px 12px !important;
                  margin: 0 auto !important;
                  justify-content: center !important;
                  align-items: center !important;
                }
              `}</style>
            </div>
          </div>
        </div>
        {/* Right column - Test instructions (1/3 width) */}
        <div className="w-1/3 flex flex-col" data-id="element-909">
          <div className="bg-white rounded-lg border border-gray-200" style={{
          height: '500px'
        }} data-id="element-910">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-911">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-912">Test agent</h2>
              {showTestLog && <button className="flex items-center text-sm text-gray-700" onClick={() => setShowTestLog(false)} data-id="element-913">
                  <RefreshCw className="w-4 h-4 mr-1" data-id="element-914" />
                  Reset Test
                </button>}
            </div>
            {!showTestLog ? <div className="p-6 flex flex-col items-center justify-center" data-id="element-915">
                <div className="w-20 h-20 mb-4" data-id="element-916">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-917">
                    <rect width="80" height="80" rx="4" fill="#E5E7EB" data-id="element-918" />
                    <rect x="22" y="18" width="36" height="44" rx="2" fill="white" stroke="#6B7280" strokeWidth="2" data-id="element-919" />
                    <rect x="28" y="26" width="24" height="4" rx="1" fill="#6B7280" data-id="element-920" />
                    <rect x="28" y="34" width="24" height="4" rx="1" fill="#6B7280" data-id="element-921" />
                    <rect x="28" y="42" width="24" height="4" rx="1" fill="#6B7280" data-id="element-922" />
                    <rect x="28" y="50" width="12" height="4" rx="1" fill="#6B7280" data-id="element-923" />
                  </svg>
                </div>
                <div className="text-sm font-medium text-gray-700 mb-1" data-id="element-924">
                  Run a test
                </div>
                <div className="text-xs text-gray-500 text-center mb-6" data-id="element-925">
                  See how the agent follows the instructions and refine if
                  needed.
                </div>
                <button className="w-full py-2 bg-gray-800 text-white rounded-md font-medium" onClick={handleRunTest} data-id="element-926">
                  Run Test
                </button>
              </div> : <div className="overflow-y-auto" style={{
            maxHeight: 'calc(500px - 57px)'
          }} data-id="element-927">
                {/* Albert introduction */}
                <div className="p-4 border-b border-gray-100" data-id="element-928">
                  <div className="flex items-start" data-id="element-929">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2 flex-shrink-0" data-id="element-930">
                      <span className="text-xs text-gray-700" data-id="element-931">A</span>
                    </div>
                    <div className="flex-1 min-w-0 px-1" data-id="element-932">
                      <div className="text-sm font-medium text-gray-800" data-id="element-933">
                        Albert
                      </div>
                      <p className="text-sm text-gray-600 mt-1 break-words" data-id="element-934">
                        Let's start testing!
                        <br data-id="element-935" />
                        First, help me out – perform the trigger action and once
                        you're done, I will proceed with the instructions.
                      </p>
                    </div>
                  </div>
                </div>
                {/* Test log items */}
                {testLogData.map(item => <div key={item.id} className="p-4 border-b border-gray-100" data-id="element-936">
                    {item.title === 'Perform trigger action' ? <>
                        <div className="font-medium text-sm text-gray-800 mb-2 px-1" data-id="element-937">
                          {item.title}
                        </div>
                        {item.details && item.details.map((detail, idx) => <div key={idx} className="flex items-start mb-3" data-id="element-938">
                              <div className="w-8 h-8 bg-gray-200 rounded-md flex items-center justify-center mr-3 flex-shrink-0" data-id="element-939">
                                {detail.icon}
                              </div>
                              <div className="px-1" data-id="element-940">
                                <div className="text-sm font-medium text-gray-800" data-id="element-941">
                                  {detail.title}
                                </div>
                                <div className="text-xs text-gray-600" data-id="element-942">
                                  {detail.subtitle}
                                </div>
                              </div>
                            </div>)}
                        <div className="bg-gray-100 p-3 rounded-md border border-gray-200 flex items-center" data-id="element-943">
                          <CheckCircle className="w-4 h-4 text-gray-700 mr-2" data-id="element-944" />
                          <span className="text-sm text-gray-800" data-id="element-945">
                            {item.success}
                          </span>
                        </div>
                      </> : item.status === 'summary' ? <>
                        <div className="flex items-center mb-1" data-id="element-946">
                          <div className="w-5 h-5 rounded-full border-2 border-gray-500 flex items-center justify-center mr-2 flex-shrink-0" data-id="element-947">
                            <span className="text-xs text-gray-500" data-id="element-948">i</span>
                          </div>
                          <span className="text-sm font-medium text-gray-800 px-1" data-id="element-949">
                            {item.title}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 ml-7 break-words px-1" data-id="element-950">
                          {item.description}
                        </p>
                        {item.id === testLogData.length && <div className="mt-4 ml-7 px-1" data-id="element-951">
                            <div className="text-sm text-gray-600 mb-2" data-id="element-952">
                              Not happy with the result?
                            </div>
                            <button className="flex items-center text-sm text-gray-700" data-id="element-953">
                              <span className="mr-2" data-id="element-954">☐</span> Give feedback
                            </button>
                            <button className="flex items-center text-sm text-gray-700 mt-3" data-id="element-955">
                              <RefreshCw className="w-4 h-4 mr-1" data-id="element-956" />
                              View Details
                            </button>
                          </div>}
                      </> : <div className="flex items-start mb-1" data-id="element-957">
                        <div className="flex-shrink-0 mt-0.5" data-id="element-958">
                          {item.status === 'success' ? <CheckCircle className="w-4 h-4 text-gray-700 mr-2" data-id="element-959" /> : <XCircle className="w-4 h-4 text-gray-700 mr-2" data-id="element-960" />}
                        </div>
                        <div className="flex-1 min-w-0 px-1" data-id="element-961">
                          <span className="text-sm font-medium text-gray-800 break-words" data-id="element-962">
                            {item.title}
                          </span>
                          {item.description && <p className="text-sm text-gray-600 mt-1 break-words" data-id="element-963">
                              {item.description}
                            </p>}
                        </div>
                      </div>}
                  </div>)}
              </div>}
          </div>
        </div>
      </div>
      {/* Add MCP Server Modal */}
      {showAddMCPModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50" data-id="element-964">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md" data-id="element-965">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-id="element-966">
              <div className="flex items-center" data-id="element-967">
                <Server className="w-5 h-5 text-gray-700 mr-2" data-id="element-968" />
                <h3 className="text-lg font-medium text-gray-800" data-id="element-969">
                  Add MCP Server
                </h3>
              </div>
              <button onClick={() => setShowAddMCPModal(false)} className="text-gray-500 hover:text-gray-700" data-id="element-970">
                <X className="w-5 h-5" data-id="element-971" />
              </button>
            </div>
            <div className="p-4 bg-gray-50 border-b border-gray-200" data-id="element-972">
              <div className="flex items-start" data-id="element-973">
                <Info className="w-5 h-5 text-gray-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-974" />
                <p className="text-sm text-gray-600" data-id="element-975">
                  Connect to an MCP server to access additional tools. You'll
                  need the server URL and API key from your MCP administrator.
                </p>
              </div>
            </div>
            <form onSubmit={e => {
          e.preventDefault();
          handleAddMCPServer({
            name: e.target.serverName.value,
            url: e.target.serverUrl.value,
            apiKey: e.target.apiKey.value
          });
          setShowAddMCPModal(false);
        }} data-id="element-976">
              <div className="p-4" data-id="element-977">
                <div className="mb-4" data-id="element-978">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-979">
                    Server Name
                  </label>
                  <input type="text" name="serverName" className="w-full p-2 border border-gray-200 rounded-md" placeholder="My MCP Server" required data-id="element-980" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-981">
                    A friendly name to identify this server
                  </p>
                </div>
                <div className="mb-4" data-id="element-982">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-983">
                    Server URL
                  </label>
                  <input type="url" name="serverUrl" className="w-full p-2 border border-gray-200 rounded-md" placeholder="https://mcp.example.com" required data-id="element-984" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-985">
                    The base URL of your MCP server
                  </p>
                </div>
                <div className="mb-4" data-id="element-986">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-987">
                    API Key
                  </label>
                  <input type="password" name="apiKey" className="w-full p-2 border border-gray-200 rounded-md" placeholder="••••••••••••••••" required data-id="element-988" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-989">
                    Authentication key for secure access to the server
                  </p>
                </div>
                <div className="mb-4" data-id="element-990">
                  <button type="button" className="text-sm text-gray-600 flex items-center" onClick={() => {}} data-id="element-991">
                    <ChevronDown className="w-4 h-4 mr-1" data-id="element-992" />
                    Advanced Settings
                  </button>
                </div>
                <button type="button" className="w-full px-4 py-2 border border-gray-200 rounded-md text-sm font-medium flex items-center justify-center mb-4" data-id="element-993">
                  <Server className="w-4 h-4 mr-2" data-id="element-994" />
                  Test Connection
                </button>
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end" data-id="element-995">
                <button type="button" onClick={() => setShowAddMCPModal(false)} className="px-4 py-2 border border-gray-200 rounded-md mr-2 text-sm font-medium" data-id="element-996">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium" data-id="element-997">
                  Add Server
                </button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
};